/**
 * Brightcove Pre-roll Ad Targeting Utility
 *
 * Extracts real-time user identity and demographic targeting data from Google Publisher
 * Tag (GPT) and Identity Service Manager (ISM) on the page, and merges these parameters
 * into the Brightcove IMA3 VAST pre-roll tag URL.
 */

/**
 * Allowed targeting keys from Identity Service Manager (ISM) to forward to GAM.
 * Mirrors dev-hm-adstack-manager targeting allowlist. Raw identifiers like 'npi'
 * are strictly excluded from outgoing ad URLs; instead, hashed 'amid' and 'nid'
 * are derived and forwarded.
 */
export const ALLOWED_ISM_TARGETING_KEYS = new Set([
	'spr',
	'sp',
	'profession',
	'ssp',
	'specialty',
	'oraclesegments',
	'oraid',
	'eid',
	'subid',
	'log',
	'usrc',
	'amid',
	'nid',
]);

/**
 * Retrieve user identity and demographic targeting data from Identity Service Manager (ISM).
 *
 * @return {Object|null} Key-value map of user targeting parameters, or null.
 */
export function getIsmUserTargeting() {
	if (typeof window === 'undefined') {
		return null;
	}

	try {
		if (typeof window.ismData?.getTargetingInfo === 'function') {
			const ismTargeting = window.ismData.getTargetingInfo();
			if (ismTargeting && typeof ismTargeting === 'object') {
				return ismTargeting;
			}
		}
	} catch (e) {
		return null;
	}

	return null;
}

// Backwards compatibility alias
export const getGptUserTargeting = getIsmUserTargeting;

/**
 * Merge live user identity targeting parameters into the VAST ad tag cust_params.
 *
 * @param {string} url The base VAST tag URL from data-ad-tag-url.
 * @return {string} Enriched VAST tag URL.
 */
export function enrichAdTagWithUserTargeting(url) {
	if (!url) {
		return '';
	}

	// Ensure a fresh, unique correlator per client request to prevent GAM page-cache collisions
	let processedUrl = url;
	if (processedUrl.includes('correlator=')) {
		processedUrl = processedUrl.replace(/([?&]correlator=)[^&]*/, `$1${Date.now()}`);
	} else {
		const sep = processedUrl.includes('?') ? '&' : '?';
		processedUrl = `${processedUrl}${sep}correlator=${Date.now()}`;
	}

	const ismTargeting = getIsmUserTargeting();
	if (!ismTargeting || typeof ismTargeting !== 'object') {
		return processedUrl;
	}

	try {
		const match = processedUrl.match(/([?&]cust_params=)([^&]*)/);
		const existingCustParams = match ? decodeURIComponent(match[2]) : '';

		// Fast lookup set of existing keys in cust_params to prevent duplicate parameters
		const existingKeys = new Set(
			existingCustParams
				.split('&')
				.map((pair) => pair.split('=')[0].toLowerCase())
				.filter(Boolean),
		);

		// Mirror dev-hm-adstack-manager: derive amid and nid using hmUtils if npi is present
		const userTargeting = { ...ismTargeting };
		if (userTargeting.npi && typeof window !== 'undefined' && window.hmUtils) {
			if (!userTargeting.amid && typeof window.hmUtils.sha256 === 'function') {
				userTargeting.amid = window.hmUtils.sha256(userTargeting.npi);
			}
			if (
				!userTargeting.nid &&
				userTargeting.amid &&
				typeof window.hmUtils.truncateNPIHash === 'function'
			) {
				userTargeting.nid = window.hmUtils.truncateNPIHash(userTargeting.amid);
			}
		}

		const clean = (val) => {
			const str = String(val).trim();
			return typeof window !== 'undefined' && window.hmUtils?.cleanTargetingValue
				? window.hmUtils.cleanTargetingValue(str)
				: str;
		};

		const extraPairs = [];
		for (const [rawKey, rawVal] of Object.entries(userTargeting)) {
			const key = rawKey.trim().toLowerCase();
			if (
				!ALLOWED_ISM_TARGETING_KEYS.has(key) ||
				existingKeys.has(key) ||
				rawVal == null ||
				rawVal === ''
			) {
				continue;
			}

			const val = Array.isArray(rawVal)
				? rawVal.filter((v) => v != null && v !== '').map(clean).join(',')
				: clean(rawVal);

			if (val) {
				extraPairs.push(encodeURIComponent(key) + '=' + encodeURIComponent(val));
			}
		}

		if (extraPairs.length === 0) {
			return processedUrl;
		}

		const combined = existingCustParams
			? existingCustParams + '&' + extraPairs.join('&')
			: extraPairs.join('&');

		const encodedCustParams = encodeURIComponent(combined);

		if (match) {
			return processedUrl.replace(/([?&]cust_params=)[^&]*/, '$1' + encodedCustParams);
		}

		const separator = processedUrl.indexOf('?') > -1 ? '&' : '?';
		return processedUrl + separator + 'cust_params=' + encodedCustParams;
	} catch (e) {
		return processedUrl;
	}
}

/**
 * Known Google / IAB sample VAST ad tag patterns that must never play in production.
 */
const SAMPLE_AD_PATTERNS = [
	'single_ad_samples',
	'iab_vast_samples',
	'sample_ct=',
	'/124319096/',
	'/6062/iab_vast_samples',
];

/**
 * Determine whether a VAST ad tag URL points to a Google or IAB sample ad tag.
 *
 * @param {string} url The VAST ad tag URL to inspect.
 * @return {boolean} True if the URL matches a known sample ad tag pattern.
 */
export function isSampleAdTagUrl(url) {
	if (!url || typeof url !== 'string') {
		return false;
	}
	const lower = url.toLowerCase();
	return SAMPLE_AD_PATTERNS.some((pattern) => lower.includes(pattern));
}

/**
 * Configure and enforce pre-roll ad tag URL on a Brightcove Video.js player instance,
 * intercepting requests before IMA3 dispatches them and blocking sample ad fallbacks.
 *
 * @param {Object}      player        Video.js player instance.
 * @param {HTMLElement} playerElement The <video-js> DOM element.
 * @return {Function} The applyAdTag handler bound to the player.
 */
export function configurePlayerPreRollAds(player, playerElement) {
	if (!player || !playerElement) {
		return null;
	}

	const getEffectiveAdTagUrl = () => {
		const rawAdTagUrl = playerElement.getAttribute('data-ad-tag-url');
		if (!rawAdTagUrl || isSampleAdTagUrl(rawAdTagUrl)) {
			return '';
		}
		const enriched = enrichAdTagWithUserTargeting(rawAdTagUrl);
		if (typeof player.ima3?.adMacroReplacement === 'function') {
			return player.ima3.adMacroReplacement(enriched);
		}
		return enriched;
	};

	const abortSampleAdPlayback = () => {
		try {
			if (player.ima3?.adsManager) {
				if (typeof player.ima3.adsManager.discardAdBreak === 'function') {
					player.ima3.adsManager.discardAdBreak();
				}
				if (typeof player.ima3.adsManager.skip === 'function') {
					player.ima3.adsManager.skip();
				}
			}
			if (typeof player.ads?.inAdBreak === 'function' && player.ads.inAdBreak()) {
				player.ads.endLinearAdMode?.();
			}
			player.removeClass?.('vjs-ad-loading');
			player.removeClass?.('ima3-ad-loading');
			player.trigger?.('adscanceled');
			player.trigger?.('nopreroll');
		} catch (e) {
			// Ignore cleanup errors if IMA3 state is mid-transition
		}
	};

	const applyAdTag = (event) => {
		const effectiveUrl = getEffectiveAdTagUrl();

		if (player.ima3 && player.ima3.settings) {
			player.ima3.settings.serverUrl = effectiveUrl;
		}

		// If IMA3 already constructed an AdsRequest before our listener ran, check if it used a sample URL
		const existingRequestUrl = player.ima3?.adsRequest?.adTagUrl;
		const hadSampleRequest = isSampleAdTagUrl(existingRequestUrl);

		if (player.ima3?.adsRequest) {
			player.ima3.adsRequest.adTagUrl = effectiveUrl;
		}

		if (event && event.adsRequest) {
			event.adsRequest.adTagUrl = effectiveUrl;
		}

		if (!effectiveUrl && (event?.type === 'ads-before-request' || hadSampleRequest)) {
			abortSampleAdPlayback();
		} else if (hadSampleRequest && player.ima3?.adsManager) {
			abortSampleAdPlayback();
		}
	};

	const guardSampleAdOnLoad = () => {
		if (!getEffectiveAdTagUrl() || isSampleAdTagUrl(player.ima3?.adsRequest?.adTagUrl)) {
			abortSampleAdPlayback();
		}
	};

	applyAdTag();

	if (!player._hmPreRollAdsBound && typeof player.on === 'function') {
		player._hmPreRollAdsBound = true;
		player.on('ima3-ready', applyAdTag);
		player.on('loadstart', applyAdTag);
		player.on('play', applyAdTag);
		player.on('ads-before-request', applyAdTag);
		player.on('ads-request', applyAdTag);
		player.on('ads-load', guardSampleAdOnLoad);
		player.on('ads-ad-started', guardSampleAdOnLoad);
	}

	return applyAdTag;
}

// Ensure global accessibility for legacy and custom integrations
if (typeof window !== 'undefined') {
	window.hmEnrichBrightcoveAdTagWithUserTargeting = enrichAdTagWithUserTargeting;
}

if (typeof module !== 'undefined' && module.exports) {
	module.exports = {
		getIsmUserTargeting,
		getGptUserTargeting,
		enrichAdTagWithUserTargeting,
		isSampleAdTagUrl,
		configurePlayerPreRollAds,
	};
}
